import { makeAutoObservable, action, observable } from 'mobx';
import { quickTempAPI, freightTempAPI, moveTempAPI, orderCalAPI, bannerSelAPI } from '../api/service';
import { PhoneRegExp } from '../lib/util';

// 접수 옵션 기본값 — payGbn 은 인성 지급방법(1 선불 · 2 착불 · 3 신용)
// 출발지·도착지는 의뢰자와 같은 항목(상호·부서명·담당자명·연락처), etcDesc 는 화물 '기타' 물품 설명(인성 메모로만 간다)
const initial = {
  startAddr: "",
  startAddrDtl: "",
  startPointCode: "",
  startCompany: "",
  startDept: "",
  startNm: "",
  startTel: "",
  endAddr: "",
  endAddrDtl: "",
  endPointCode: "",
  endCompany: "",
  endDept: "",
  endNm: "",
  endTel: "",
  etcDesc: "",
  vehicleType: "Bike",
  orderType: "",
  reservationTime: "",
  route: "OneWay",
  paymentStatus: "",
  requests: "",
  payGbn: '1',
};

export default class QuickService {
  root;
  cmStore;
  loginStore;
  payResult = {
    orderDetails: [],
    orderNumber: "",
    totalValue: 0,
  };
  orderResult = {
    isPayment: "",
    orderDetails: null,
    totalValue: 0,
  };
  smallBoxTotalCnt = 0;
  options = { ...initial };
  banner = null;
  submitting = false;


  constructor(root) {
    this.root = root;
    this.cmStore = this.root.cmStore;
    this.loginStore = this.root.loginStore;

    makeAutoObservable(
      this, {
        options: observable,
        orderResult: observable,
        submitting: observable,
        quickerReg: action,
        orderCalAPI: action
      }
    )
  }

  handleOptions = (name, value) => {
    this.options[name] = value;

    /**
     * 서류(documentCnt)
     * 소박스(smallBoxCnt)  1
     * 중박스(heavyBoxCnt)  3
     * 대박스(greateBoxCnt) 4
     *
     * 소박스 5개 이상 부터는 오토바이 금지
     */
    if (name === "smallBoxCnt" || name === "heavyBoxCnt" || name === "greateBoxCnt") {
      const { smallBoxCnt, heavyBoxCnt, greateBoxCnt } = this.options;
      let cnt = 0;

      if (smallBoxCnt) cnt = cnt + parseInt(smallBoxCnt);
      if (heavyBoxCnt) cnt = cnt + (parseInt(heavyBoxCnt) * 3);
      if (greateBoxCnt) cnt = cnt + (parseInt(greateBoxCnt) * 4);
      this.smallBoxTotalCnt = cnt;
    }

  }

  initialOption = () => {
    this.options = { ...initial };
    this.orderResult = {
      isPayment: "",
      orderDetails: null,
      totalValue: 0,
    };
  }

  // 공통 스토어의 주소·의뢰자 정보를 접수 옵션에 싣는다
  addressSet = () => {
    const { toAddress, toBcode, fromAddress, fromBcode, etcAddress, requester } = this.root.cmStore;
    this.options["startAddr"] = toAddress;
    this.options["startPointCode"] = toBcode;
    this.options["endAddr"] = fromAddress;
    this.options["endPointCode"] = fromBcode;
    const SIDE_KEYS = [
      'startAddrDtl', 'startCompany', 'startDept', 'startNm', 'startTel',
      'endAddrDtl', 'endCompany', 'endDept', 'endNm', 'endTel', 'addressName',
    ];
    SIDE_KEYS.forEach((key) => { this.options[key] = etcAddress[key] || ''; });

    this.options["requesterCompany"] = requester.company;
    this.options["requesterDept"] = requester.dept;
    this.options["requesterName"] = requester.name;
    this.options["requestTel"] = requester.tel;
    this.options["requesterAddr"] = requester.addr;
    this.options["requesterAddrDtl"] = requester.addrDtl;
    this.options["requesterBcode"] = requester.bcode;
  }

  // 접수 — 주소·의뢰자·연락처를 확인하고 종류별 접수 API 로 넘긴다. 서버가 인성에 바로 접수한다
  quickerReg = async (pageType) => {
    if (this.submitting) return;
    this.addressSet();

    const {
      startAddr, startAddrDtl, endAddr, endAddrDtl,
      startNm, startTel, endNm, endTel,
      requesterName, requestTel,
    } = this.options;
    let msg = "";

    if (!requesterName) msg = "의뢰자 담당자명을 입력하세요.";
    else if (!requestTel) msg = "의뢰자 연락처를 입력하세요.";
    else if (!PhoneRegExp(requestTel)) msg = "의뢰자 연락처를 지역번호를 포함한 전화번호 또는 핸드폰번호로 정확히 입력해 주세요.";
    else if (!startAddr) msg = "출발지 주소를 입력하세요.";
    else if (!startAddrDtl) msg = "출발지 상세 주소를 입력하세요.";
    else if (!startNm) msg = "출발지 담당자명을 입력하세요.";
    else if (!startTel) msg = "출발지 연락처를 입력하세요.";
    else if (!PhoneRegExp(startTel)) msg = "출발지 연락처를 정확히 입력해 주세요.";
    else if (!endAddr) msg = "도착지 주소를 입력하세요.";
    else if (!endAddrDtl) msg = "도착지 상세 주소를 입력하세요.";
    else if (!endNm) msg = "도착지 담당자명을 입력하세요.";
    else if (!endTel) msg = "도착지 연락처를 입력하세요.";
    else if (!PhoneRegExp(endTel)) msg = "도착지 연락처를 정확히 입력해 주세요.";

    if (msg) {
      alert(msg);
      return;
    }

    if (pageType === "cargo") {
      await this.cargoTempAPICall();
    } else if (pageType === 'move') {
      await this.moveTempAPICall();
    } else {
      await this.quickTempAPICall();
    }
  }

  // 접수 API 호출과 완료 화면 이동 — 세 종류가 같이 쓴다
  submitOrder = async (api) => {
    this.submitting = true;
    try {
      const resp = await api(this.options);
      const { data, status } = resp || {};
      if (status !== 201) {
        alert((data && data.message) || '접수 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return false;
      }
      this.payResult = data;
      window.location.href = "/success";
      return true;
    } catch (error) {
      console.log('%c##### Error-submitOrder: ', 'color: red', error);
      alert('접수 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      return false;
    } finally {
      this.submitting = false;
    }
  }

  quickTempAPICall = async () => {
    let msg = "";
    const { userInfo } = this.loginStore;
    const { documentCnt, smallBoxCnt, heavyBoxCnt, greateBoxCnt, agree } = this.options;

    if (!documentCnt && !smallBoxCnt && !heavyBoxCnt && !greateBoxCnt) {
      msg = "물량 및 수량을 선택하세요.";
    } else if (documentCnt === "0" && smallBoxCnt === "0" && heavyBoxCnt === "0" && greateBoxCnt === "0") {
      msg = "물량 및 수량을 선택하세요.";
    } else if (!userInfo && (!agree || agree === 'N')) {
      msg = "개인정보 수집 이용 동의해 주세요.";
    }

    if (msg) {
      alert(msg);
      return;
    }
    await this.submitOrder(quickTempAPI);
  }

  cargoTempAPICall = async () => {
    let msg = "";
    const { userInfo } = this.loginStore;
    const { paletteCnt, fuCnt, boxCnt, etcCnt, etcDesc, vehicleType, vehicleOptionType, agree } = this.options;

    if (!paletteCnt && !fuCnt && !boxCnt && !etcCnt) {
      msg = "물량 및 수량을 선택하세요.";
    } else if (paletteCnt === "0" && fuCnt === "0" && boxCnt === "0" && etcCnt === "0") {
      msg = "물량 및 수량을 선택하세요.";
    } else if (parseInt(etcCnt, 10) > 0 && !(etcDesc || '').trim()) {
      msg = "기타 물품이 어떤 물건인지 입력하세요.";
    } else if (!vehicleType || vehicleType === "") {
      msg = "차량종류를 선택하세요.";
    } else if (!vehicleOptionType || vehicleOptionType === "") {
      msg = "차량옵션를 선택하세요.";
    } else if (!userInfo && (!agree || agree === 'N')) {
      msg = "개인정보 수집 이용 동의해 주세요.";
    }

    if (msg) {
      alert(msg);
      return;
    }
    await this.submitOrder(freightTempAPI);
  }

  moveTempAPICall = async () => {
    delete this.options.isExpress;
    delete this.options.route;
    const { userInfo } = this.loginStore;
    let msg = "";
    const {
      reservationTime, vehicleType, loadingMethod,
      dropMethod, moveStartType, moveEndType,
      moveType, startFloor, endFloor, agree
    } = this.options;

    if (!moveStartType) msg = "엘리베이터 유무를 선택하세요";
    else if (!moveEndType) msg = "엘리베이터 유무를 선택하세요";
    else if (!startFloor) msg = "출발지 층수를 선택하세요.";
    else if (!endFloor) msg = "도착지 층수를 선택하세요.";
    else if (!moveType) msg = "이사 규모를 선택하세요.";
    else if (!reservationTime) msg = "이사 날짜를 선택하세요.";
    else if (!loadingMethod) msg = "상차 방법을 선택하세요.";
    else if (!dropMethod) msg = "하차 방법을 선택하세요.";
    else if (!userInfo && (!agree || agree === 'N')) {
      msg = "개인정보 수집 이용 동의해 주세요.";
    }

    if (vehicleType === 'dd') delete this.options["vehicleType"];

    if (msg) {
      alert(msg);
      return;
    }
    await this.submitOrder(moveTempAPI);
  }

  // 예상 운임 조회 — 화면 표시용. 실제 운임은 인성 접수 뒤 정해진다
  orderCalAPI = async () => {
    this.addressSet();

    try {
      const params = { ...this.options };

      if (params.workerCnt && params.workerCnt > 0) {
        params.workerCnt = 1;
      }
      const resp = await orderCalAPI(params);
      const { status, data } = resp || {};
      if (status !== 200) throw resp;

      this.orderResult = data;

      if (this.options.route === 'LayOver') {
        this.orderResult.isPayment = 'N';
      }
      this.options.paymentStatus = '';
    } catch (error) {
      console.log('%c##### Error-orderCalAPI: ', 'color: red', error);
    }
  }

  bannerSelCall = async() => {
    try {
      const resp = await bannerSelAPI();
      const { status, data } = resp || {};
      if (status !== 200) throw resp;

      this.banner = data;

    } catch (error) {

    }
  }
}
